import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Calendar,
  TimePicker,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Select
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import eventsService from "../../services/eventsService";
import lookupService from "../../services/lookupService";
import './eventDetails.css';

import { Notify } from "../../services/custom/Messages";
import { recordError } from "../../utils/logService";
import { openNotification } from "../custom";
import { Organisations } from "../organizations";
import { Venues } from "./venue/Venues";

import { 
  confirmAction,
  DataCapturerCard,
  eventCreationModeEnum,
  PageHeaderControl,
  util
} from "@seims/shared";

import {
  EVENT_TYPES_STORE_NAME,
  EVENT_GROUPS_STORE_NAME,
  TARGET_PARTICIPANTS_STORE_NAME,
  EVENT_OBJECTIVES_STORE_NAME,
  ORGANISER_EXPERIENCE_STORE_NAME,
} from "../../constants"
import { isInProgressOrApproved, isSubmitted } from "./util/eventStatusConfig";


const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const hasFundingAndHost = (partners = []) => {
  let hasFunding = false;
  let hasHost = false;

  partners.forEach((org) => {
    if (!Array.isArray(org.contributions)) return;

    org.contributions.forEach((c) => {
      const label =
        typeof c === "object" && typeof c.label === "string"
          ? c.label.toLowerCase()
          : typeof c === "string"
            ? c.toLowerCase()
            : "";

      if (label.includes("fund")) hasFunding = true;
      if (label.includes("host")) hasHost = true;
    });
  });

  return hasFunding && hasHost;
};

export const AddEditEvent = ({ api }) => {

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const isEdit = !!eventId;
  const organisationsRef = useRef(null);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [eventTypes, setEventTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  // const [venues, setVenues] = useState([]);

  const [admins, setAdmins] = useState([]);

  const [targetParticipants, setTargetParticipants] = useState([]);
  const [eventObjectives, setEventObjectives] = useState([]);
  const [organiserExperience, setOrganiserExperience] = useState([]);

  const [otherParticipantChecked, setOtherParticipantChecked] = useState(false);
  const [otherObjectiveChecked, setOtherObjectiveChecked] = useState(false);


  const [selectedOrganization, setSelectedOrganization] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hideButtons, setHideButtons] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [originalStartDate, setOriginalStartDate] = useState(null);
  const [originalEndDate, setOriginalEndDate] = useState(null);

  const [openStartPicker, setOpenStartPicker] = useState(false);
  const [openEndPicker, setOpenEndPicker] = useState(false);

  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();

  const { eventName: locationEventName, eventStatus } = location.state || {};

  const createTitle = (text, nullableValue) => {
    return nullableValue ? `${text} - ${nullableValue}` : `${text}`;
  };

  // View-only mode if navigated via performEventAction("view", ...)
  const isViewMode = location.pathname.startsWith("/events/view/");
  let pageTitle = "Create New Event";
  if (isViewMode) {
    pageTitle = createTitle("View", locationEventName);
  } else if (isEdit) {
    pageTitle = createTitle("Edit", locationEventName);
  }


  const selectedEventGroup = Form.useWatch("groupOrTheme", form);
  const selectedEventType = Form.useWatch("type", form);
  const selectedParticipants = Form.useWatch("targetParticipants", form) || [];
  const selectedObjectives = Form.useWatch("objectives", form) || [];
  const selectedVenueFormValue = Form.useWatch("venue", form) ?? null;

  const otherParticipantOption = targetParticipants.find((p) => p.value.toLowerCase() === "other");
  const otherParticipantKey = otherParticipantOption?.key;
  const otherObjectiveOption = eventObjectives.find((o) => o.value.toLowerCase() === "other");
  const otherObjectiveKey = otherObjectiveOption?.key;

  const showOtherParticipant =
    (otherParticipantKey !== undefined &&
      selectedParticipants.includes(otherParticipantKey)) ||
    (isEdit && form.getFieldValue("otherParticipant"));
  const showOtherObjective =
    (otherObjectiveKey !== undefined &&
      selectedObjectives.includes(otherObjectiveKey)) ||
    (isEdit && form.getFieldValue("objectivesOther"));

  const otherEventGroupKey = groups.find(
    (t) => t.value?.toLowerCase() === "other"
  )?.key?.toString();

  const otherEventTypeKey = eventTypes.find(
    (t) => t.value?.toLowerCase() === "other"
  )?.key?.toString();

  const showOtherType =
    selectedEventType === otherEventTypeKey;

  const showOtherGroup =
    selectedEventGroup === otherEventGroupKey;

  let msg = "";

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const pageSize = useMemo(() => (screens.xxl ? 8 : 6), [screens]);
  const venueValue = form.getFieldValue("venue");
  const venueErrorRef = useRef(null);
  const venuesRef = useRef(null);

  useEffect(() => setCurrentPage(1), [pageSize]);

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    if (isEdit &&
      eventTypes.length &&
      groups.length
    ) {
      setDisclaimerAccepted(true);
      loadEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, eventTypes, groups, eventId]);


  useEffect(() => {
    if (!showOtherType) {
      form.setFieldsValue({ otherType: undefined });
    }
    if (!showOtherGroup) {
      form.setFieldsValue({ otherGroupOrTheme: undefined });
    }
  }, [showOtherType, showOtherGroup, form]);


  useEffect(() => {
    if (!otherParticipantChecked) {
      form.setFieldValue("otherParticipant", undefined);
    }
    if (!otherObjectiveChecked) {
      form.setFieldValue("objectivesOther", undefined);
    }
  }, [otherParticipantChecked, otherObjectiveChecked,]);


  useEffect(() => {
    form.setFieldValue("organisations", selectedOrganization);
  }, [selectedOrganization, form]);

  useEffect(() => {
    if (isMobile) {
      setShowOrgSelector(true);
    } else {
      setShowOrgSelector(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (submitAttempted && !form.getFieldValue("venue")) {
      // wait for DOM paint
      setTimeout(() => {
        if (venueErrorRef.current) {
          venueErrorRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else {
          venuesRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 150);
    }
  }, [submitAttempted]);

  // useEffect(() => {
  //   const startDefault = dayjs().hour(6).minute(0).second(0);
  //   const endDefault = dayjs().hour(18).minute(0).second(0);

  //   form.setFieldsValue({
  //     startDate: startDefault,
  //     endDate: endDefault,
  //   });

  //   setTempStartDate(startDefault);
  //   setTempEndDate(endDefault);
  // }, []);

  const onNameBlur = (e) => {
    const { value } = e.target;
    const textValue = util.toPascalText(value);
    form.setFieldsValue({ "name": textValue });
  }

  const renderEventDetails = () => (
    <Card
      className="event-details-card"
      variant="borderless"
      // className="event-card-borderless"
      classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
      title={<span className="event-card-borderless-header-text">Event Details</span>}
    >
      <Row gutter={[16, 8]}
        style={{
          paddingBottom: "12px"
        }}>
        {/* Event Name – READ ONLY */}
        <Col xs={24} >
          <Form.Item
            label="Event Name"
            name="name"
            rules={[
              { required: true, message: "Event name is required" },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length < 3) {
                    return Promise.reject(new Error("Must be at least 3 non-space characters"));
                  }
                  return Promise.resolve();
                },
              },
              { max: 1500, message: "Event name cannot exceed 1500 characters" },

            ]}
            onBlur={onNameBlur}
          >
            <Input maxLength={1500} showCoun disabled={!isAddEditMode} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 16]} >
        {/* Start Date & Time – EDITABLE */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Event Start Date & Time"
            name="startDate"

            getValueProps={(value) => ({
              value: value
                ? dayjs(value).format("DD/MM/YYYY HH:mm")
                : "",
            })}

            rules={[
              { required: true, message: "Start date & time is required" },
              () => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();

                  const today = dayjs().startOf("day");
                  const newStart = dayjs(value).startOf("day");

                  if (!isEdit || !originalStartDate) {
                    if (newStart.isBefore(today)) {
                      return Promise.reject(
                        new Error("Start date must be today or in the future")
                      );
                    }
                    return Promise.resolve();
                  }

                  const original = dayjs(originalStartDate).startOf("day");

                  if (!newStart.isBefore(today)) return Promise.resolve();
                  if (newStart.isSame(original)) return Promise.resolve();
                  if (newStart.isAfter(original)) return Promise.resolve();

                  return Promise.reject(
                    new Error("Cannot move start date further into the past")
                  );
                },
              }),
            ]}
          >
            <Input
              readOnly
              placeholder="Select start date"
              disabled={!isLimitedEditMode && !isAddEditMode}
              onClick={() => {
                if (!isLimitedEditMode && !isAddEditMode) return;

                const current = form.getFieldValue("startDate");

                const parsed = current
                  ? dayjs(current)
                  : dayjs().hour(6).minute(0).second(0);

                setTempStartDate(parsed);
                setOpenStartPicker(true);
              }}
            />
          </Form.Item>
        </Col>

        {/* End Date & Time – EDITABLE */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Event End Date & Time"
            name="endDate"
            dependencies={["startDate"]}

            getValueProps={(value) => ({
              value: value
                ? dayjs(value).format("DD/MM/YYYY HH:mm")
                : "",
            })}

            rules={[
              { required: true, message: "End date & time is required" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();

                  const start = getFieldValue("startDate");
                  if (!start) return Promise.resolve();

                  const startDateTime = dayjs(start, "DD/MM/YYYY HH:mm");
                  const endDateTime = dayjs(value, "DD/MM/YYYY HH:mm");

                  const sameTime =
                    startDateTime.format("HH:mm") === endDateTime.format("HH:mm");

                  if (sameTime) {
                    return Promise.reject(
                      new Error("End time cannot be the same as the start time. Please choose a later time")
                    );
                  }

                  if (endDateTime.isBefore(startDateTime)) {
                    return Promise.reject(
                      new Error("End date cannot be before start date")
                    );
                  }

                  return Promise.resolve();
                },
              })
            ]}
          >
            <Input
              readOnly
              placeholder="Select end date"
              disabled={!isLimitedEditMode && !isAddEditMode}
              onClick={() => {
                if (!isLimitedEditMode && !isAddEditMode) return;

                const current = form.getFieldValue("endDate");

                const parsed = current
                  ? dayjs(current)
                  : dayjs().hour(18).minute(0).second(0);

                setTempEndDate(parsed);
                setOpenEndPicker(true);
              }}
            />
          </Form.Item>
        </Col>

        {/* Event Type – READ ONLY */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Event Type"
            name="type"
            rules={[{ required: true, message: "Event type is required" }]}
          >
            <Select
              disabled={!isAddEditMode}
              options={eventTypes.map(g => ({
                label: g.value,
                value: String(g.key),
              }))}
            />
          </Form.Item>

          {showOtherType && (
            <Form.Item
              name="otherType"
              label="Please specify"
              rules={[{ required: true }]}
            >
              <Input disabled={!isAddEditMode} />
            </Form.Item>
          )}
        </Col>
        {/* Event Group – READ ONLY */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Event Group"
            name="groupOrTheme"
            rules={[{ required: true, message: "Event Group/Theme is required" }]}
          >
            <Select
              disabled={!isAddEditMode}
              options={groups.map(g => ({
                label: g.value,
                value: String(g.key),
              }))}
            />
          </Form.Item>

          {showOtherGroup && (
            <Form.Item
              name="otherGroupOrTheme"
              label="Please specify"
              rules={[{ required: true, message: "Other Group/Theme is required" }]}
            >
              <Input disabled={!isAddEditMode} />
            </Form.Item>
          )}
        </Col>
        {/* Description – READ ONLY */}
        <Col xs={24}>
          <Form.Item
            label="Event Description"
            name="description"
            rules={[
              { required: true, message: "Event description is required" },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length < 3) {
                    return Promise.reject(new Error("Must be at least 3 non-space characters"));
                  }
                  return Promise.resolve();
                },
              },
              { max: 1500, message: "Event description cannot exceed 1500 characters" },

            ]}
          >
            <Input.TextArea rows={4} maxLength={1500} showCount disabled={!isAddEditMode} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  const renderTargetParticipants = () => (
    <Card
      variant="borderless"
      className="event-card-borderless"
      classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
      title={<span className="event-card-borderless-header-text">Target Participants</span>}
    >
      <Row gutter={[8, 6]}>
        {targetParticipants.map((p) => {
          const selected = selectedParticipants.includes(p.key);

          return (
            <Col xs={24} md={24} lg={12} xl={12} key={p.key}>
              <Checkbox
                className="mobile-check-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 0",
                  cursor: isAddEditMode ? "pointer" : "not-allowed",
                }}
                onClick={() => {
                  if (!isAddEditMode) return;

                  const next = selected
                    ? selectedParticipants.filter((v) => v !== p.key)
                    : [...selectedParticipants, p.key];

                  form.setFieldValue("targetParticipants", next);

                  if (p.key === otherParticipantKey && selected) {
                    form.setFieldValue("otherParticipant", undefined);
                  }
                }}
                checked={selected} disabled={!isAddEditMode}><span>{p.value}</span></Checkbox>
            </Col>
          );
        })}
      </Row>

      {showOtherParticipant && (
        <Form.Item
          name="otherParticipant"
          label="Please specify"
          style={{ marginTop: 16 }}
          rules={[
            {
              required: true,
              message: "Please specify the other participant",
            },
          ]}
        >
          <Input size="large" disabled={!isAddEditMode} />
        </Form.Item>
      )}

      <Form.Item
        name="targetParticipants"
        rules={[
          {
            validator: (_, v) =>
              Array.isArray(v) && v.length > 0
                ? Promise.resolve()
                : Promise.reject(
                  new Error("Please select at least one target participant")
                ),
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        {/* Dummy element – AntD needs a child */}
        <div />
      </Form.Item>
    </Card>
  );

  const renderObjectives = () => (
    <Card
      variant="borderless"
      className="event-card-borderless"
      classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
      title={<span className="event-card-borderless-header-text">Objectives</span>}
    >
      {eventObjectives.map((o) => {
        const selected = selectedObjectives.includes(o.key);

        return (
          <Checkbox
            key={o.key}
            className="mobile-check-row"
            onClick={() => {
              if (!isAddEditMode) return;

              const next = selected
                ? selectedObjectives.filter(v => v !== o.key)
                : [...selectedObjectives, o.key];

              form.setFieldValue("objectives", next);

              if (o.key === otherObjectiveKey && selected) {
                form.setFieldValue("objectivesOther", undefined);
              }
            }}
            checked={selected} disabled={!isAddEditMode} ><span>{o.value}</span></Checkbox>
        );
      })}

      {showOtherObjective && (
        <Form.Item
          name="objectivesOther"
          label="Please specify"
          rules={[
            {
              required: true,
              message: "Please specify the other objective",
            },
          ]}
        >
          <Input size="large" disabled={!isAddEditMode} />
        </Form.Item>
      )}
      <Form.Item
        name="objectives"
        rules={[
          {
            validator: (_, v) =>
              Array.isArray(v) && v.length > 0
                ? Promise.resolve()
                : Promise.reject(
                  new Error("Please select at least one objective")
                ),
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <div />
      </Form.Item>
    </Card>
  );

  const renderExperienceAndIncident = () => (
    <Card
      variant="borderless"
      className="event-card-borderless"
      classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
      title={<span className="event-card-borderless-header-text">Experience & Incident History</span>}
    >

      {/* Experience */}
      <Form.Item
        label="How many years of experience do you have organising such events?"
        name="years"
        rules={[
          {
            required: true,
            message: "Experience is required",
          },
        ]}
      >
        <Select
          disabled={!isAddEditMode}
          size="large"
          placeholder="Select experience"
          options={organiserExperience.map((ex) => ({
            label: ex.value,
            value: String(ex.key),
          }))}
        />
      </Form.Item>

      {/* Incident */}
      <Form.Item
        label="Have you ever had an incident at a past event? If so, please specify what happened."
        name="incidentDescription"
        rules={[
          {
            required: true,
            message: "Incident description is required",
          },
        ]}
      >
        <Input.TextArea
          disabled={!isAddEditMode}
          rows={4}
          placeholder="Describe the incident (if any)"
        />
      </Form.Item>
    </Card>
  );

  const loadLookups = async () => {
    try {
      const [
        typesResp,
        groupsResp,
        targetParcResp,
        eventObjectivesResp,
        organizerExperienceResp,
      ] = await Promise.all([
        lookupService.getLookup(EVENT_TYPES_STORE_NAME, { useCache: true }),
        lookupService.getLookup(EVENT_GROUPS_STORE_NAME, { useCache: true }),
        lookupService.getLookup(TARGET_PARTICIPANTS_STORE_NAME, { useCache: true }),
        lookupService.getLookup(EVENT_OBJECTIVES_STORE_NAME, { useCache: true }),
        lookupService.getLookup(ORGANISER_EXPERIENCE_STORE_NAME, { useCache: true }),
      ]);

      setEventTypes(typesResp?.data ?? []);
      setGroups(groupsResp?.data ?? []);
      setTargetParticipants(targetParcResp?.data ?? []);
      setEventObjectives(
        (eventObjectivesResp?.data ?? []).sort((a, b) => a.key - b.key)
      );
      setOrganiserExperience(organizerExperienceResp?.data ?? []);
    } catch (err) {
      recordError(err);
    }
  };

  // const loadVenues = async () => {
  //   setLoadingVenues(true);
  //   const venuesResp = await lookupService.getEventVenues();
  //   setVenues(venuesResp?.data ?? []);
  //   setLoadingVenues(false);
  // };

  const loadEvent = async () => {

    const res = await eventsService.getEventById(eventId);
    if (res?.status !== 200) return;

    const event = res.data;

    const mappedPartners = (event.partners ?? []).map(p => ({
      ...p,

      contributions: (p.contributions ?? []).map(c => ({
        label: c.value,
        value: c.key.toString(),
      })),

      otherContribution: p.otherContribution ?? null,
    }));

    setOriginalStartDate(dayjs(event.startDate));
    setOriginalEndDate(dayjs(event.endDate));
    setSelectedVenueId(event.venue?.id)
    setSelectedVenue(event.venue?.id)

    form.setFieldsValue({
      name: event.name,
      description: event.description,

      type: String(event.type?.key),
      groupOrTheme: String(event.groupOrTheme?.key),
      venue: String(event.venue?.id),

      startDate: dayjs(event.startDate),
      endDate: dayjs(event.endDate),


      type: String(event.type?.key),
      otherType: event.otherType ?? undefined,

      groupOrTheme: String(event.groupOrTheme?.key),
      otherGroupOrTheme: event.otherGroupOrTheme ?? undefined,


      targetParticipants: event.targetParticipants?.map(p => p.key),
      otherParticipant: event.otherParticipant ?? undefined,

      objectives: event.objectives?.map(o => o.key),
      objectivesOther: event.otherObjective ?? undefined,

      years: String(event.experienceInfo?.years?.key),
      incidentDescription: event.experienceInfo?.details,

    });
    setAdmins(res.data.facilitators ?? []);

    setSelectedOrganization(mappedPartners);
    form.setFieldValue("organisations", mappedPartners);
    // setEventStatus(event.status?.value);

  };

  const handleSelectedOrgs = (keys) => {
    setSelectedRowKeys(keys);
  };

  const handleHideButtons = (value) => {
    setHideButtons(value);
  };

  const handleSelectedOrganization = (selectedOrg) => {

  console.log("ORG CHANGE:", selectedOrg);
  console.log("submitAttempted BEFORE reset:", submitAttempted);

    setSelectedOrganization((prevSelection) => {
      const updatedSelection = (() => {
        if (!Array.isArray(prevSelection)) return selectedOrg ?? [];
        if (!selectedOrg || selectedOrg.length === 0) return [];


        if (Array.isArray(selectedOrg) && selectedOrg.length > 0) {
          setSubmitAttempted(false);
        }

        if (Array.isArray(selectedOrg) && selectedOrg.length > 0 && hasFundingAndHost(selectedOrg)
        ) {
          setSubmitAttempted(false); // clears both errors
        }

        let orgIndex = 0;
        let Ids = [];

        if (Array.isArray(selectedOrg)) {
          Ids = selectedOrg.map((x) => x.id);
          orgIndex = prevSelection.findIndex((org) =>
            Ids.includes(org.id)
          );
        } else {
          orgIndex = prevSelection.findIndex(
            (org) => org.id === selectedOrg.id
          );
        }

        if (orgIndex === -1) {
          return [...prevSelection, selectedOrg];
        }

        return prevSelection.map((org, index) =>
          index === orgIndex
            ? {
              ...org,
              contributions: selectedOrg.contributions,
              otherContribution: selectedOrg.otherContribution,
            }
            : org
        );
      })();

      // Sync to form
      form.setFieldValue("organisations", updatedSelection);

      // CRITICAL: re-trigger validation when contributions change

      form
        .validateFields(["organisations"]).then(() => {
          setSubmitAttempted(false);
        }).catch(() => { });

      // Clear submit error state once valid
      if (
        updatedSelection.some(
          (org) =>
            Array.isArray(org.contributions) &&
            org.contributions.some(
              (c) =>
                c?.label?.toLowerCase() === "funding" ||
                c?.value?.toString() === "16"
            )
        )
      ) {
        setSubmitAttempted(false);
      }

      return updatedSelection;
    });
    setSubmitAttempted(false)
  };

  const handleRemoveOrganization = (orgId) => {
    setSelectedOrganization((prev) => {
      const next = prev.filter((org) => org.id !== orgId);

      //  sync table selection
      setSelectedRowKeys(next.map((org) => org.id.toString()));

      //  sync hidden form field (CRITICAL)
      form.setFieldValue("organisations", next);

      return next;
    });
  };

  const onSubmit = async (values) => {

    if (isSubmitting) return;
    setIsSubmitting(true);

  console.log("submitAttempted BEFORE:", submitAttempted);
  console.log("selectedOrganization:", selectedOrganization);

    setSubmitAttempted(true);
      console.log("submitAttempted AFTER set:", true);

    if (!selectedOrganization || selectedOrganization.length === 0) return;
    if (!hasFundingAndHost(selectedOrganization)) return;

    const isFullEdit = isSubmitted(eventStatus);

    const buildEventPayload = (values) => {
      const otherParticipant = targetParticipants.find(p =>
        p.value.toLowerCase().includes("other")
      );

      const otherObjective = eventObjectives.find(o =>
        o.value.toLowerCase().includes("other")
      );

      return {
        ...(isEdit ? { id: Number(eventId) } : {}),

        // ALWAYS required by backend
        name: isFullEdit ? values.name : form.getFieldValue("name"),
        description: isFullEdit
          ? values.description
          : form.getFieldValue("description"),

        type: isFullEdit ? values.type : form.getFieldValue("type"),
        otherType: isFullEdit
          ? values.otherType || null
          : form.getFieldValue("otherType") || null,

        groupOrTheme: isFullEdit
          ? values.groupOrTheme
          : form.getFieldValue("groupOrTheme"),

        otherGroupOrTheme: isFullEdit
          ? values.otherGroupOrTheme || null
          : form.getFieldValue("otherGroupOrTheme") || null,

        // Editable in all allowed states
        startDate: values.startDate?.toISOString(),
        endDate: values.endDate?.toISOString(),
        venue: values.venue,

        // Objectives
        objectives: isFullEdit
          ? values.objectives
          : form.getFieldValue("objectives"),

        otherObjective: isFullEdit
          ? otherObjective &&
            values.objectives?.includes(otherObjective.key)
            ? values.objectivesOther
            : null
          : form.getFieldValue("objectivesOther") || null,

        // Target participants
        targetParticipants: isFullEdit
          ? values.targetParticipants
          : form.getFieldValue("targetParticipants"),

        otherParticipant: isFullEdit
          ? otherParticipant &&
            values.targetParticipants?.includes(otherParticipant.key)
            ? values.otherParticipant
            : null
          : form.getFieldValue("otherParticipant") || null,

        // Experience
        experienceInfo: isFullEdit
          ? {
            years: Number(values.years),
            details: values.incidentDescription,
          }
          : {
            years: Number(form.getFieldValue("years")),
            details: form.getFieldValue("incidentDescription"),
          },

        // Admins ALWAYS editable
        facilitators: admins.map(a => ({
          id: a.id,
          name: a.name,
          surname: a.surname,
          email: a.email,
          registerd: true,
        })),

        // Partners (unchanged)
        partners: selectedOrganization.map(org => ({
          id: org.id,
          contributions: org.contributions.map(c =>
            typeof c === "object" ? c.value : c
          ),
          otherContribution: org.otherContribution || null,
        })),

        ratified: disclaimerAccepted,
      };
    };

    try {
      const payload = buildEventPayload(values);

      if (isEdit) {
        await eventsService.updateEvent(eventId, payload);
      } else {
        await eventsService.createEvent(payload);
      }

      openNotification(
        "Event",
        `Event ${isEdit ? "updated" : "created"} successfully`,
        Notify.SUCCESS
      );

      navigate("/events");
    } catch (error) {
      recordError(error);
      openNotification("Event", "Failed to submit event", Notify.ERROR);
    } finally {
      setIsSubmitting(false);
    }

  };

  let eventCreationMode = eventCreationModeEnum.ADD;

  if (isViewMode) {
    eventCreationMode = eventCreationModeEnum.VIEW;
  } else if (isInProgressOrApproved(eventStatus)) {
    eventCreationMode = eventCreationModeEnum.EDIT_LIMITED;
  } else if (isEdit || isSubmitted(eventStatus)) {
    eventCreationMode = eventCreationModeEnum.EDIT;
  }

  const isAddEditMode = [eventCreationModeEnum.ADD, eventCreationModeEnum.EDIT].includes(eventCreationMode);
  const isLimitedEditMode = [eventCreationModeEnum.EDIT_LIMITED].includes(eventCreationMode);

  const handleCancelClick = () => {
    confirmAction({
      title: "Discard changes?",
      description: "Are you sure you want to cancel? All unsaved changes will be lost.",
      cancelText: "No",
      okText: "Yes, discard",
      onOk: () => navigate("/"),
    });
  };

  return (

    <div
      style={{
        padding: 10,
      }}
    >
      <PageHeaderControl title={pageTitle} showConfirmBack={isAddEditMode || isLimitedEditMode} >
        <Modal
          footer={null}
          open={!disclaimerAccepted && !isEdit}
          closable={false}
          maskClosable={false}
          bodyStyle={{ padding: 0 }}
          transitionName="disclaimer-modal"
          maskTransitionName="fade"

        >
          <div>
            <h3 className="disclaimer-title">Disclaimer</h3>

            <p className="disclaimer-text">
              I hereby acknowledge and agree that I have taken all reasonable actions to ensure that the event
              I am registering is of a high quality and that I have performed all the necessary checks to ensure that:
            </p>

            <ol className="disclaimer-list">
              <li>The event will comply with all South African laws and regulations.</li>
              <li>The event will comply with applicable Occupational Health and Safety laws and regulations.</li>
              <li>The event will source the necessary consent and indemnities from the event participants, where applicable.</li>
            </ol>

            <div className="disclaimer-actions">
              <Button
                className="disclaimer-btn-outline"
                onClick={() => navigate("/events")}
              >
                I do not accept
              </Button>

              <Button
                type="primary"
                className="disclaimer-btn-primary"
                onClick={() => setDisclaimerAccepted(true)}
              >
                I accept
              </Button>
            </div>
          </div>
        </Modal>

        {(!isAddEditMode && !isViewMode) && (
          <Alert
            type="info"
            showIcon
            message="Only event dates, venue, and administrators can be edited at this stage."
            style={{ marginBottom: 16 }}
          />
        )}

        {isViewMode && (
          <Alert
            type="info"
            showIcon
            message="This event is in view-only mode."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form
          layout="vertical"
          form={form}
          disabled={isViewMode || (!disclaimerAccepted && !isEdit)}
          onFinish={(values) => {
            setSubmitAttempted(true);
            onSubmit(values);
          }}

          onFinishFailed={({ errorFields }) => {
            console.log("FORM FAILED", errorFields);
            setSubmitAttempted(true);

            const venueValue = form.getFieldValue("venue");
            const organisationsValue = form.getFieldValue("organisations");

            // 1. Scroll to Venue if missing
            if (!venueValue && venueErrorRef.current) {
              venueErrorRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              return;
            }

            // 2. Scroll to Partners if invalid/missing
            if (
              !Array.isArray(organisationsValue) ||
              organisationsValue.length === 0 ||
              !hasFundingAndHost(organisationsValue)
            ) {
              organisationsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
              return;
            }

            // 3. Otherwise scroll to first AntD validation error
            if (errorFields.length > 0) {
              form.scrollToField(errorFields[0].name, {
                behavior: "smooth",
                block: "center",
              });
            }
          }}
        >

          <Row justify="center">
            {/* <Col xs={24} sm={24} md={22} lg={18} xl={16}> */}
            <Col xs={24}>
              {/* Event Details */}
              {renderEventDetails()}

              <Form.Item
                name="venue"
                rules={[{ required: true, message: "Venue is required" }]}
                hidden
              >
                <Input />
              </Form.Item>
              <Card
                variant="borderless"
                className="event-card-borderless"
                classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
              >
              <div ref={venuesRef}>
                <Venues
                  eventId={eventId}
                  disabled={!isLimitedEditMode && !isAddEditMode}
                  selectedVenueId={selectedVenueFormValue}
                  onSelectVenue={(venue) => {
                    form.setFieldsValue({
                      venue: venue ? venue.id : null,
                    });

                    //trigger re-render (important for validation UI)
                    form.validateFields(["venue"]).catch(() => { });
                  }}
                  api={eventsService}
                />
                {submitAttempted && !venueValue && (
                  <div ref={venueErrorRef}>
                    <Alert
                      type="error"
                      showIcon
                      message="Please select or create a venue"
                       style={{ marginTop: 16 }}
                    />
                  </div>
                )}
              </div>
           </Card>
              {/* Administrators */}
              <DataCapturerCard existingAdmins={admins} exportAdmins={setAdmins} />

              {/* Participants and Objectives */}
              {isMobile ? (
                <>
                  {/* Mobile layout */}
                  {renderTargetParticipants()}
                  {renderObjectives()}
                </>
              ) : (
                <Row gutter={48}>
                  <Col span={12}>{renderTargetParticipants()}</Col>
                  <Col span={12}>{renderObjectives()}</Col>
                </Row>
              )}

              {/* Partners */}
              <Form.Item
                name="organisations"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!Array.isArray(value) || value.length === 0) {
                        return Promise.reject(
                          new Error("At least one partner must be selected")
                        );
                      }

                      if (!hasFundingAndHost(value)) {
                        return Promise.reject(
                          new Error("Please select at least one Funding partner and one Host partner")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                hidden
              >
                <Input />
              </Form.Item>
              <Card
                variant="borderless"
                className="event-card-borderless"
                classNames={{ header: "event-card-borderless-header", body: "event-card-borderless-body" }}
              >
             
                <div ref={organisationsRef}>

                  <Organisations
                    isEdit={isEdit}
                    mode={eventCreationMode}
                    api={eventsService}
                    onHideButtons={handleHideButtons}
                    onSelectedOrganization={handleSelectedOrganization}
                    onRemoveOrganization={handleRemoveOrganization}
                    selectedOrganization={selectedOrganization}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={handleSelectedOrgs}
                    setSelectedGlobalOrganization={setSelectedOrganization}
                    titleStyle="card"
                    organizationBelongsTo="event"
                  />
                  {submitAttempted &&
                    (!Array.isArray(selectedOrganization) ||
                      selectedOrganization.length === 0) && (
                      <Alert
                        type="error"
                        showIcon
                        message="At least one partner must be selected"
                        style={{ marginBottom: 16 }}
                      />
                    )}

                  {submitAttempted &&
                    Array.isArray(selectedOrganization) &&
                    selectedOrganization.length > 0 &&
                    !hasFundingAndHost(selectedOrganization) && (
                      <Alert
                        type="error"
                        showIcon
                        message="Please select at least one Funding partner and one Host partner"
                        style={{ marginBottom: 16 }}
                      />
                    )}

                </div>
              </Card>
              {/* Additional Questions */}
              {renderExperienceAndIncident()}
              {/* Submit */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                {isViewMode ? (
                  /*  View mode: direct navigation, NO Popconfirm */
                  <Button
                    type="default"
                    style={{
                      borderRadius: 20,
                      padding: "6px 20px",
                    }}
                    onClick={() => navigate("/events")}
                    disabled={!isViewMode}
                  >
                    Cancel
                  </Button>
                ) : (
                  /* Edit/Add mode: Popconfirm */
                  <Button
                    type="default"
                    onClick={handleCancelClick}
                    style={{ borderRadius: 20, padding: "6px 20px" }}
                  >
                    Cancel
                  </Button>

                )}
                {!isViewMode && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{
                      background: "#1868A5",
                      borderRadius: 20,
                    }}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isEdit ? "Update Event" : "Submit Event"}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </PageHeaderControl>
<Modal
  open={openStartPicker}
  footer={null}
  onCancel={() => setOpenStartPicker(false)}
  centered
>
  <Calendar
    fullscreen={false}
    value={tempStartDate}
    onSelect={(date) => {
      const base = tempStartDate || dayjs().hour(6).minute(0);

      setTempStartDate(
        base
          .year(date.year())
          .month(date.month())
          .date(date.date())
      );
    }}
  />

  <TimePicker
    value={tempStartDate}
    format="HH:mm"
    style={{ width: "100%", marginTop: 16 }}
    onChange={(time) => {
      if (!time) return;

      const base = tempStartDate || dayjs().hour(6).minute(0);

      setTempStartDate(
        base
          .hour(time.hour())
          .minute(time.minute())
      );
    }}
  />

  <div style={{ marginTop: 16, textAlign: "right" }}>
    <Button onClick={() => setOpenStartPicker(false)} style={{ marginRight: 8 }}>
      Cancel
    </Button>

    <Button
      type="primary"
      onClick={() => {
        if (!tempStartDate) return;

        form.setFieldValue("startDate", tempStartDate);
        form.validateFields(["startDate", "endDate"]);

        setOpenStartPicker(false);
      }}
    >
      OK
    </Button>
  </div>
</Modal>

<Modal
  open={openEndPicker}
  footer={null}
  onCancel={() => setOpenEndPicker(false)}
  centered
>
  <Calendar
    fullscreen={false}
    value={tempEndDate}
    onSelect={(date) => {
      const base = tempEndDate || dayjs().hour(18).minute(0);

      setTempEndDate(
        base
          .year(date.year())
          .month(date.month())
          .date(date.date())
      );
    }}
  />

  <TimePicker
    value={tempEndDate}
    format="HH:mm"
    style={{ width: "100%", marginTop: 16 }}
    onChange={(time) => {
      if (!time) return;

      const base = tempEndDate || dayjs().hour(18).minute(0);

      setTempEndDate(
        base
          .hour(time.hour())
          .minute(time.minute())
      );
    }}
  />

  <div style={{ marginTop: 16, textAlign: "right" }}>
    <Button onClick={() => setOpenEndPicker(false)} style={{ marginRight: 8 }}>
      Cancel
    </Button>

    <Button
      type="primary"
      onClick={() => {
        if (!tempEndDate) return;

        form.setFieldValue("endDate", tempEndDate);
        form.validateFields(["endDate"]);

        setOpenEndPicker(false);
      }}
    >
      OK
    </Button>
  </div>
</Modal>

    </div>
  );
}